// Pente relative mensuelle d'une série (`scoring.md` § 1, `trend_slope_12m`) : moindres carrés
// divisés par la moyenne. Réimplémentée ici (le SPA n'importe pas `@radar/core`) pour les
// sparklines des mots-clés, dont le contrat ne porte pas de pente.

/** `null` si moins de 3 points ou moyenne nulle. `+0,03` = +3 % par mois. */
export function trendSlope(points: readonly number[]): number | null {
  const n = points.length;
  if (n < 3) return null;
  const meanX = (n - 1) / 2;
  let sum = 0;
  for (const value of points) sum += value;
  const meanY = sum / n;
  if (meanY <= 0) return null;
  let cov = 0;
  let variance = 0;
  points.forEach((value, index) => {
    cov += (index - meanX) * (value - meanY);
    variance += (index - meanX) ** 2;
  });
  if (variance === 0) return null;
  return cov / variance / meanY;
}

/** Interpolation linéaire des points manquants ; les bords prennent le voisin connu le plus proche. */
export function interpolatePoints(points: readonly (number | null | undefined)[]): number[] {
  const known = points
    .map((value, index) => ({ index, value }))
    .filter(
      (p): p is { index: number; value: number } =>
        typeof p.value === 'number' && Number.isFinite(p.value),
    );
  if (known.length === 0) return [];
  const first = known[0];
  const last = known[known.length - 1];
  if (!first || !last) return [];
  return points.map((value, index) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (index < first.index) return first.value;
    if (index > last.index) return last.value;
    let before = first;
    let after = last;
    for (const point of known) {
      if (point.index < index) before = point;
      if (point.index > index) {
        after = point;
        break;
      }
    }
    const span = after.index - before.index;
    if (span === 0) return before.value;
    return before.value + ((after.value - before.value) * (index - before.index)) / span;
  });
}

/** Nombre de points réellement connus (non interpolés). */
export function knownPointCount(points: readonly (number | null | undefined)[]): number {
  return points.filter((value) => typeof value === 'number' && Number.isFinite(value)).length;
}
